import { WalletKit } from '@reown/walletkit'
import { Core } from '@walletconnect/core'
import { buildApprovedNamespaces, getSdkError } from '@walletconnect/utils'
import { ethers } from 'ethers'
import { TokenboundClient } from '@tokenbound/sdk'
import { bytesToBigInt, Chain, encodePacked, hexToBytes, keccak256 } from 'viem'
import { mainNetwork, metadata } from '@/components/Context'
import { projectId } from '@/app/crazy/config'
import blockchainConfig from '@/config.json'
import TBA_NFT_ABI from '../abis/TbaNft.json'
import WALLET_BASE from '../abis/WalletBase.json'

const PROJECT_ID = projectId
const CHAIN_ID = mainNetwork.chainId
const CHAIN = `eip155:${CHAIN_ID}`
const ERC6551_REGISTRY_ADDRESS = '0x000000006551c19487814612e58FE06813775758'
const chainConfig =blockchainConfig[CHAIN_ID]
const CRAZY_NFT_ADDRESS = chainConfig.nft.address

export type WalletKitInstance = InstanceType<typeof WalletKit>

let instance: WalletKitInstance | null = null

const pulseChain: Chain = {
    id: CHAIN_ID,
    name: mainNetwork.name,
    nativeCurrency: { name: 'Test PLS', symbol: mainNetwork.currency, decimals: 18 },
    rpcUrls: {
        default: { http: [mainNetwork.rpcUrl] },
        public: { http: [mainNetwork.rpcUrl] },
    },
    testnet: true,
}

function calculateSalt(prefix: string, tokenId: string | number): bigint {
    return (
        bytesToBigInt(hexToBytes(keccak256(encodePacked(['string', 'uint256'], [prefix, BigInt(tokenId)])))) &
        BigInt('0x1fffffffffffff')
    )
}

async function resolveTBA(walletProvider: ethers.Eip1193Provider, tokenId: string) {
    const provider = new ethers.BrowserProvider(walletProvider)
    const signer = await provider.getSigner()
    const crazyNft = new ethers.Contract(CRAZY_NFT_ADDRESS, TBA_NFT_ABI.abi, signer)

    const personalWalletImplementation = (await crazyNft.getPersonalWalletImplementation()) as `0x${string}`

    const client = new TokenboundClient({
        signer,
        chainId: CHAIN_ID,
        implementationAddress: personalWalletImplementation,
        registryAddress: ERC6551_REGISTRY_ADDRESS,
        chain: pulseChain,
    })
    const salt = calculateSalt('personal', tokenId)
    return client.getAccount({ tokenContract: CRAZY_NFT_ADDRESS, tokenId, chainId: CHAIN_ID, salt: Number(salt) })
}

export async function getWalletKit(): Promise<WalletKitInstance> {
    if (instance) return instance

    const core = new Core({ projectId: PROJECT_ID })
    instance = await WalletKit.init({
        core,
        metadata: metadata,
    })

    return instance
}

export function resetWalletKitSingleton(): void {
    console.log('Resetting WalletKit singleton')
    instance = null
}

export async function resetWalletKit(kit: WalletKitInstance): Promise<void> {
    const sessions = Object.values(kit.getActiveSessions())
    for (const s of sessions) {
        await kit.disconnectSession({ topic: s.topic, reason: getSdkError('USER_DISCONNECTED') })
    }

    const pairings = kit.core.pairing.getPairings()
    for (const p of pairings) {
        await kit.core.pairing.disconnect({ topic: p.topic })
        // (kit.core.pairing as any).deletePairing?.(p.topic, getSdkError('USER_DISCONNECTED'))
    }

    indexedDB.deleteDatabase('walletconnect')
    indexedDB.deleteDatabase('WALLET_CONNECT_V2_INDEXEDDB')

    Object.keys(localStorage)
        .filter((k) => k.startsWith('wc@2:') || k.startsWith('@appkit/'))
        .forEach((k) => localStorage.removeItem(k))

    //    resetWalletKitSingleton()
}

export async function hasSessionForToken(
    kit: WalletKitInstance,
    tokenId: string,
    walletProvider: ethers.Eip1193Provider,
): Promise<boolean> {
    const tba = await resolveTBA(walletProvider, tokenId) // 0xTokenBoundAddr
    const accountId = `${CHAIN}:${tba}`

    const sessions = Object.values(kit.getActiveSessions())
    return sessions.some((s) => Object.values(s.namespaces).some((ns) => ns.accounts.includes(accountId)))
}

export async function connectWalletKitWithHandlers({
    tokenId,
    walletProvider,
    onConnected,
    onDisconnected,
}: {
    tokenId: string
    walletProvider: ethers.Eip1193Provider
    onConnected?: () => void
    onDisconnected?: () => void
}): Promise<WalletKitInstance> {
    const kit = await getWalletKit()

    kit.on('session_proposal', async (proposal) => {
        try {
            const tbaAddress = await resolveTBA(walletProvider, tokenId)

            const namespaces = buildApprovedNamespaces({
                proposal: proposal.params,
                supportedNamespaces: {
                    eip155: {
                        chains: [CHAIN],
                        methods: ['eth_sendTransaction', 'personal_sign'],
                        events: ['accountsChanged', 'chainChanged'],
                        accounts: [`${CHAIN}:${tbaAddress}`],
                    },
                },
            })

            await kit.approveSession({ id: proposal.id, namespaces })
            onConnected?.()
        } catch (err) {
            console.error('Session proposal failed', err)
            await kit.rejectSession({ id: proposal.id, reason: getSdkError('USER_REJECTED') })
        }
    })

    kit.on('session_request', async ({ topic, params, id }) => {
        const request = params.request
        const provider = new ethers.BrowserProvider(walletProvider)
        const signer = await provider.getSigner()

        try {
            switch (request.method) {
                case 'personal_sign': {
                    const sig = await provider.send('personal_sign', [request.params[0], await signer.getAddress()])
                    await kit.respondSessionRequest({ topic, response: { id, jsonrpc: '2.0', result: sig } })
                    break
                }
                case 'eth_sendTransaction': {
                    const txParams = request.params[0]
                    const tba = new ethers.Contract(txParams.from, WALLET_BASE.abi, signer)
                    const tx = await tba.execute(txParams.to, txParams.value ?? 0, txParams.data, 0)
                    await kit.respondSessionRequest({ topic, response: { id, jsonrpc: '2.0', result: tx.hash } })
                    break
                }
                default:
                    await kit.respondSessionRequest({
                        topic,
                        response: {
                            id,
                            jsonrpc: '2.0',
                            error: { code: -32000, message: `Unsupported method: ${request.method}` },
                        },
                    })
            }
        } catch (err) {
            console.error('Session request failed', err)
        }
    })

    kit.on('session_delete', () => {
        resetWalletKit(kit)
        onDisconnected?.()
    })

    return kit
}
